import React, { useState } from "react";
import PropTypes from "prop-types";
import ChevronRightIcon from "mdi-react/ChevronRightIcon";
import ChevronLeftIcon from "mdi-react/ChevronLeftIcon";

const paginationStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.75rem 0",
  },
  info: {
    fontSize: "0.8125rem",
    color: "#374151",
  },
  nav: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "0.375rem",
    border: "1px solid #d1d5db",
    overflow: "hidden",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "2rem",
    height: "2rem",
    padding: "0 0.625rem",
    fontSize: "0.8125rem",
    fontWeight: "500",
    border: "none",
    borderRight: "1px solid #d1d5db",
    backgroundColor: "#fff",
    color: "#374151",
    cursor: "pointer",
    transition: "background-color 0.15s ease, color 0.15s ease",
  },
  buttonHover: {
    backgroundColor: "#f3f4f6",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  buttonActive: {
    backgroundColor: "#052654",
    color: "#fff",
    borderColor: "#052654",
  },
  buttonLast: {
    borderRight: "none",
  },
};

const PaginationButton = ({ onClick, disabled, active, isLast, children }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      style={{
        ...paginationStyles.button,
        ...(isLast ? paginationStyles.buttonLast : {}),
        ...(active ? paginationStyles.buttonActive : {}),
        ...(disabled ? paginationStyles.buttonDisabled : {}),
        ...(!active && !disabled && hovered ? paginationStyles.buttonHover : {}),
      }}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </button>
  );
};

const ReactTablePagination = ({
  dataLength,
  page,
  gotoPage,
  canPreviousPage,
  pageOptions,
  pageSize,
  pageIndex,
  previousPage,
  nextPage,
  canNextPage,
  setPageSize,
  manualPageSize,
  pageLength,
  pageCount,
  showFirstPage,
  showLastPage,
  showJumpToPage,
}) => {
  const [jumpValue, setJumpValue] = useState("");
  const [jumpError, setJumpError] = useState("");
  const handleJump = () => {
    const trimmed = jumpValue.trim();
    const parsed = parseInt(trimmed, 10);
    if (!trimmed || isNaN(parsed) || !/^\d+$/.test(trimmed) || parsed < 1 || parsed > pageCount) {
      setJumpError(`Page must be between 1 and ${pageCount}`);
      return;
    }
    setJumpError("");
    gotoPage(parsed - 1);
  };

  const arrayPageIndex =
    pageIndex - 2 < 0
      ? pageOptions.slice(0, pageIndex + 3)
      : pageOptions.slice(pageIndex - 2, pageIndex + 3);

  const showingFrom = pageSize * pageIndex + 1;
  const showingTo =
    pageSize * pageIndex + (pageLength ? pageLength : page.length);

  return (
    <div className="rt-pagination" style={paginationStyles.container}>
      <div className="rt-pagination__meta" style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
        <p className="rt-pagination__info" style={{ ...paginationStyles.info, margin: 0 }}>
          Showing <strong>{showingFrom}</strong> to <strong>{showingTo}</strong> of{" "}
          <strong>{dataLength}</strong> results
        </p>
        {/* <span className="rt-pagination__sep" style={{ color: "#d1d5db", fontSize: "1rem" }}>|</span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
          <span style={{ ...paginationStyles.info, margin: 0 }}>Items per page:</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{
              height: "2rem",
              fontSize: "0.8125rem",
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              padding: "0 0.375rem",
              backgroundColor: "#fff",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div> */}
      </div>
      <nav className="rt-pagination__nav" style={paginationStyles.nav}>
        {showFirstPage && pageCount > 0 && (
          <PaginationButton
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            ⟪ First
          </PaginationButton>
        )}
        <PaginationButton
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          <ChevronLeftIcon size={16} />
          <span style={{ marginLeft: "0.25rem" }}>Previous</span>
        </PaginationButton>
        {arrayPageIndex.map((i) => (
          <PaginationButton
            key={i}
            onClick={() => gotoPage(i)}
            active={pageIndex === i}
          >
            {i + 1}
          </PaginationButton>
        ))}
        <PaginationButton
          onClick={nextPage}
          disabled={!canNextPage}
          isLast={!showLastPage}
        >
          <span style={{ marginRight: "0.25rem" }}>Next</span>
          <ChevronRightIcon size={16} />
        </PaginationButton>
        {showLastPage && pageCount > 0 && (
          <PaginationButton
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            isLast
          >
            Last ⟫
          </PaginationButton>
        )}
      </nav>
      {showJumpToPage && pageCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", marginLeft: "1rem" }}>
          <span style={{ ...paginationStyles.info, margin: 0 }}>Go to page:</span>
          <input
            type="number"
            min={1}
            max={pageCount}
            value={jumpValue}
            onChange={(e) => { setJumpValue(e.target.value); setJumpError(""); }}
            onKeyDown={(e) => {
              if (e.key === "." || e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
              if (e.key === "Enter") handleJump();
            }}
            style={{
              width: "4rem",
              height: "2rem",
              fontSize: "0.8125rem",
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              padding: "0 0.375rem",
              textAlign: "center",
            }}
          />
          <button
            type="button"
            onClick={handleJump}
            style={{
              height: "2rem",
              padding: "0 0.625rem",
              fontSize: "0.8125rem",
              fontWeight: "500",
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              backgroundColor: "#fff",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            Go
          </button>
          {jumpError && (
            <span style={{ fontSize: "0.8125rem", color: "#dc2626" }}>{jumpError}</span>
          )}
        </div>
      )}
    </div>
  );
};

ReactTablePagination.propTypes = {
  dataLength: PropTypes.number.isRequired,
  page: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  gotoPage: PropTypes.func.isRequired,
  canNextPage: PropTypes.bool.isRequired,
  canPreviousPage: PropTypes.bool.isRequired,
  pageOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  pageSize: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired,
  previousPage: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  manualPageSize: PropTypes.arrayOf(PropTypes.number),
  pageCount: PropTypes.number,
  showFirstPage: PropTypes.bool,
  showLastPage: PropTypes.bool,
  showJumpToPage: PropTypes.bool,
};

ReactTablePagination.defaultProps = {
  manualPageSize: [10, 20, 30, 40],
  pageCount: 0,
  showFirstPage: false,
  showLastPage: false,
  showJumpToPage: false,
};

export default ReactTablePagination;
